import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Download, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const ImportPlants = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  const handleImport = async () => {
    setIsImporting(true);
    setImportResult(null);

    try {
      console.log("🚀 Calling import-plants edge function...");
      
      const { data, error } = await supabase.functions.invoke('import-plants', {
        body: {}
      });

      if (error) throw error;

      console.log("✅ Import completed:", data);
      setImportResult(data.summary);
      
      toast.success("Import terminé !", {
        description: `${data.summary.success} plantes importées avec succès`
      });
    } catch (error: any) {
      console.error("❌ Import error:", error);
      toast.error("Erreur lors de l'import", {
        description: error.message || "Une erreur est survenue"
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 p-6">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-6 h-6 text-green-600" />
              Importer les Plantes Médicinales
            </CardTitle>
            <CardDescription>
              Importez 21 plantes médicinales supplémentaires dans la bibliothèque
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Cette opération va importer les plantes suivantes :
              </p>
              <ul className="text-sm space-y-1 max-h-40 overflow-y-auto">
                <li>• Angelica sinensis (Angélique chinoise)</li>
                <li>• Apium graveolens (Céleri)</li>
                <li>• Arctium lappa (Grande bardane)</li>
                <li>• Astragalus membranaceus (Astragale)</li>
                <li>• Atropa belladonna (Belladone)</li>
                <li>• Azadirachta indica (Neem)</li>
                <li>• Barosma betulina (Buchu)</li>
                <li>• Bupleurum chinense (Buplèvre)</li>
                <li>• Calendula officinalis (Souci)</li>
                <li>• Capsicum frutescens (Piment de Cayenne)</li>
                <li>• Chamaelirium luteum (Hélonias)</li>
                <li>• Cinchona spp. (Quinquina)</li>
                <li>• Cinnamomum verum (Cannelier)</li>
                <li>• Crataegus monogyna (Aubépine)</li>
                <li>• Eleutherococcus senticosus (Éleuthérocoque)</li>
                <li>• Equisetum arvense (Prêle)</li>
                <li>• Eucalyptus globulus (Eucalyptus)</li>
                <li>• Filipendula ulmaria (Reine des prés)</li>
                <li>• Foeniculum vulgare (Fenouil)</li>
                <li>• Gentiana lutea (Gentiane)</li>
                <li>• Ginkgo biloba (Ginkgo)</li>
              </ul>
            </div>

            {!importResult && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Les plantes déjà existantes seront automatiquement ignorées.
                </AlertDescription>
              </Alert>
            )}

            {importResult && (
              <div className="space-y-3">
                <Alert>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    <strong>Import terminé avec succès !</strong>
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 mx-auto text-green-600 mb-2" />
                    <div className="text-2xl font-bold text-green-600">
                      {importResult.success}
                    </div>
                    <div className="text-xs text-muted-foreground">Importées</div>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg">
                    <AlertCircle className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                    <div className="text-2xl font-bold text-blue-600">
                      {importResult.skipped}
                    </div>
                    <div className="text-xs text-muted-foreground">Ignorées</div>
                  </div>

                  <div className="p-4 bg-red-50 rounded-lg">
                    <XCircle className="w-6 h-6 mx-auto text-red-600 mb-2" />
                    <div className="text-2xl font-bold text-red-600">
                      {importResult.errors}
                    </div>
                    <div className="text-xs text-muted-foreground">Erreurs</div>
                  </div>
                </div>

                {importResult.errorDetails && importResult.errorDetails.length > 0 && (
                  <Alert variant="destructive">
                    <XCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="font-semibold mb-2">Erreurs détectées :</div>
                      <ul className="text-xs space-y-1">
                        {importResult.errorDetails.map((error: string, idx: number) => (
                          <li key={idx}>• {error}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <Button
              onClick={handleImport}
              disabled={isImporting}
              className="w-full"
              size="lg"
            >
              {isImporting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-5 w-5" />
                  Lancer l'import
                </>
              )}
            </Button>

            {importResult && (
              <Button
                onClick={() => window.location.href = '/medicinal-plants-library'}
                variant="outline"
                className="w-full"
              >
                Voir la bibliothèque
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImportPlants;
